import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

/**
 * DomainsSection — which institutions may register on this deployment.
 *
 * Disabling a domain stops new registrations and enrollments from it. Nobody
 * who already has an account is affected: the check runs at registration and
 * enrollment, never at login. The UI says so, because "remove UW-Stout" reads
 * like it might log 400 people out.
 */
export default function DomainsSection() {
  const { apiFetch } = useAuth();

  const [data,   setData]   = useState(null);
  const [domain, setDomain] = useState('');
  const [label,  setLabel]  = useState('');
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]  = useState('');
  const [done,   setDone]   = useState('');

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setDone(''); }
    else         { setDone(msg);  setError(''); }
    setTimeout(() => { setDone(''); setError(''); }, 5000);
  };

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/domains');
      if (res.ok) setData(await res.json());
      else setError(`Could not load the domain list (HTTP ${res.status}).`);
    } catch (err) { setError(err.message); }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!domain.trim()) return;
    setBusy(true);
    try {
      const res = await apiFetch('/api/admin/domains', {
        method: 'POST',
        body: JSON.stringify({ domain: domain.trim(), label: label.trim() || null }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { flash(d.message || 'Added.'); setDomain(''); setLabel(''); await load(); }
      else flash(typeof d.detail === 'string' ? d.detail : 'Could not add that domain.', true);
    } catch (err) { flash(err.message, true); }
    finally { setBusy(false); }
  };

  const toggle = async (row) => {
    setBusy(true);
    try {
      const res = await apiFetch(`/api/admin/domains/${row.id}/active`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !row.is_active }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { flash(d.message || 'Updated.'); await load(); }
      else flash(typeof d.detail === 'string' ? d.detail : 'Could not update that domain.', true);
    } catch (err) { flash(err.message, true); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Remove ${row.domain} from the list?\n\n` +
      `New accounts and enrollments from ${row.domain} will be rejected. ` +
      `Existing accounts keep working — this never affects login.`
    )) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/api/admin/domains/${row.id}`, { method: 'DELETE' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { flash(d.message || 'Removed.'); await load(); }
      else flash(typeof d.detail === 'string' ? d.detail : 'Could not remove that domain.', true);
    } catch (err) { flash(err.message, true); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Allowed Email Domains</h3>
        {error ? <div className={styles.error}>{error}</div>
               : <p className={styles.empty}>Loading…</p>}
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Allowed Email Domains</h3>
      <p style={{ fontSize: 12, color: 'var(--txt-dim)', marginBottom: 12, lineHeight: 1.5 }}>
        Which institutions can create accounts here. Matching is exact — add
        <code style={{ margin: '0 4px' }}>student.example.edu</code>
        separately if an institution issues addresses from a subdomain.
        <br />
        Disabling or removing a domain blocks new registrations and enrollments
        only. Nobody with an account loses access.
      </p>

      {done  && <div className={styles.success} style={{ marginBottom: 10 }}>✓ {done}</div>}
      {error && <div className={styles.error}   style={{ marginBottom: 10 }}>⚠ {error}</div>}

      {data.using_config_fallback && (
        <div style={{
          fontSize: 12, marginBottom: 12, padding: '10px 12px', borderRadius: 6,
          background: 'var(--amber, #f5a524)15', color: 'var(--txt-dim)',
          border: '1px solid var(--amber, #f5a524)44', lineHeight: 1.5,
        }}>
          No active domains in the table, so the deployment is falling back to
          the configured list: <strong>{data.config_domains.join(', ')}</strong>.
          Add one below to take over from the config.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
        {data.domains.map(row => (
          <div key={row.id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 13, padding: '8px 12px', borderRadius: 6,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            opacity: row.is_active ? 1 : 0.55,
          }}>
            <span style={{
              fontFamily: 'var(--font-mono, monospace)',
              color: row.is_active ? 'var(--txt)' : 'var(--txt-faint)',
            }}>
              {row.domain}
            </span>
            {row.label && (
              <span style={{ color: 'var(--txt-dim)', fontSize: 12 }}>{row.label}</span>
            )}
            {!row.is_active && (
              <span style={{ fontSize: 11, color: 'var(--txt-faint)' }}>disabled</span>
            )}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className="btn-ghost" onClick={() => toggle(row)} disabled={busy}
                style={{ fontSize: 11, padding: '3px 8px' }}>
                {row.is_active ? 'Disable' : 'Enable'}
              </button>
              <button className="btn-ghost" onClick={() => remove(row)} disabled={busy}
                style={{ fontSize: 11, padding: '3px 8px', color: 'var(--red, #e5484d)' }}>
                Remove
              </button>
            </span>
          </div>
        ))}
        {data.domains.length === 0 && (
          <p className={styles.empty}>No domains configured yet.</p>
        )}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label>Domain</label>
          <input value={domain} onChange={e => setDomain(e.target.value)}
            placeholder="uwstout.edu" autoComplete="off" spellCheck={false} />
        </div>
        <div className={styles.field}>
          <label>Institution (optional)</label>
          <input value={label} onChange={e => setLabel(e.target.value)}
            placeholder="UW-Stout" />
        </div>
      </div>
      <button className="btn-primary" onClick={add} disabled={busy || !domain.trim()}
        style={{ marginTop: 12 }}>
        Add Domain
      </button>
    </section>
  );
}
